import React, { useEffect, useState } from 'react'

import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router-dom'

import ScoresView from '@components/myScores/ScoresView'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import NonIdealState from '@components/shared/NonIdealState'
import { CollectionType } from '@models/Collection'
import { isProduct } from '@models/util/product.util'
import { COLLECTION_ITEM_PAGE_SIZE } from '@pages/home/loaders/collection.loader'
import SelectionProvider from '@providers/SelectionProvider'
import { useDialogStore } from '@stores/dialog.store'
import ApiRoutes from '@utils/api-routes'
import http from '@utils/http'
import Storage from '@utils/storage'

import ControlArea from './ControlArea'
import AddToCollectionButton from './ControlArea/AddToCollectionButton'
import ReorderButton from './ControlArea/ReorderButton'
import DeleteCollectionButton from './ControlArea/collection/DeleteCollectionButton'
import EditCollectionDetailsButton from './ControlArea/collection/EditCollectionDetailsButton'
import RemoveFromCollectionButton from './ControlArea/collection/RemoveFromCollectionButton'
import OpenArchivedScoreDialog from './OpenArchivedScoreDialog'

import type { ScoreViewOptions } from './utils/types'
import type { CollectionDocumentItem, CollectionProductItem } from '@models/CollectionItem'
import type {
  BasicCollection,
  CollectionItemsResponse,
} from '@pages/home/loaders/collection.loader'
import type { ViewType } from '@utils/global'

interface Props {
  collection: BasicCollection
  initialData: CollectionItemsResponse
}

const CollectionView: React.FC<Props> = ({ collection, initialData }: Props) => {
  const { t } = useTranslation()
  const openDialog = useDialogStore((s) => s.openDialog)

  const fetcher = useFetcher()

  const [loading, setLoading] = useState(false)
  const [itemsData, setItemsData] = useState(initialData)
  const [layoutType, setLayoutType] = useState(Storage.scoresViewType)
  const setViewType = (type: ViewType): void => {
    setLayoutType(type)
    Storage.scoresViewType = type
  }

  const { collection_items: items, remaining, startIndex } = itemsData

  // Used to detect when to infinite scroll
  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    async () => {
      if (!remaining || loading) return

      setLoading(true)
      const [newResp] = await http.post<CollectionItemsResponse>(
        ApiRoutes.collections.itemsPath(),
        {
          collection_id: collection.id,
          max_count: COLLECTION_ITEM_PAGE_SIZE,
          start_index: startIndex,
        }
      )

      if (newResp?.data) {
        setItemsData((prevData) => ({
          ...newResp.data,
          collection_items: [...prevData.collection_items, ...newResp.data.collection_items],
        }))
      }
      setLoading(false)
    },
    {
      triggerOnNoScroll: true,
      offset: 32,
    }
  )

  // Reset the data when we get something new loaded
  useEffect(() => {
    setItemsData(initialData)
  }, [initialData])

  if (!items.length) {
    return (
      <NonIdealState
        title={t('content.empty.collection.title')}
        subtitle={t('content.empty.collection.subtitle')}
        icon="folder"
      />
    )
  }

  const collectionType = items[0].type
  if (collectionType !== CollectionType.DOCUMENT && collectionType !== CollectionType.PRODUCT) {
    // TODO: How do we want to display non-document based collections?
    return null
  }

  const options: ScoreViewOptions = {
    hideCollections: true,
    beforeNavigate: (score) => {
      if (isProduct(score) || !score.archived) return true

      openDialog('open_archived', {
        state: { external_id: score.external_id },
      })
      return false
    },
    sortable: true,
    onSort: (startPos, endPos) => {
      fetcher.submit(
        {
          collectionId: collection.id.toString(),
          startPos: startPos.toString(),
          endPos: endPos.toString(),
          type: collectionType,
        },
        { method: 'post', action: '/home/updateCollectionOrder' }
      )
    },
  }

  return (
    <SelectionProvider initialItems={items}>
      <ControlArea
        setViewType={setViewType}
        viewType={layoutType}
        hideOrdering
        additionalActions={
          <>
            <EditCollectionDetailsButton collection={collection} />
            <DeleteCollectionButton collection={collection} />
            <ReorderButton />
          </>
        }>
        <RemoveFromCollectionButton
          collectionId={collection.id}
          collectionType={items.length > 0 ? items[0].type : null}
        />
        <AddToCollectionButton />
      </ControlArea>
      <div className="flex-1 overflow-y-auto w-full pb-4" ref={scrollRef}>
        <ScoresView
          scores={items as (CollectionDocumentItem | CollectionProductItem)[]}
          viewType={layoutType}
          options={options}
        />
        {remaining && loading && <LoadingSpinner />}
      </div>
      <OpenArchivedScoreDialog />
    </SelectionProvider>
  )
}

export default CollectionView
